import { HEALTH_CONFIG } from '../config/healthConfig';

export function deriveMasses(entry) {
  const weight = Number(entry?.weightKg || 0);
  const bf = Number(entry?.bodyFatPct || 0) / 100;
  const fatMass = weight * Math.max(0, Math.min(1, bf));
  const leanMass = Math.max(0, weight - fatMass);
  return { fatMassKg: fatMass, leanMassKg: leanMass };
}

// Build basic series from daily entries (ascending by id)
export function buildSeries(dailies) {
  const weight = [];
  const bodyFatPct = [];
  const fatMass = [];
  const leanMass = [];
  const calories = [];
  for (const d of (dailies || [])) {
    const wRaw = d?.weightKg;
    const bfRaw = d?.bodyFatPct;
    const wNum = Number(wRaw);
    const bfNum = Number(bfRaw);
    const hasW = wRaw != null && wRaw !== '' && Number.isFinite(wNum);
    const hasBf = bfRaw != null && bfRaw !== '' && Number.isFinite(bfNum);
    if (hasW && wNum !== 0) {
      weight.push({ id: d.id, value: wNum });
    }
    if (hasBf && bfNum !== 0) {
      bodyFatPct.push({ id: d.id, value: bfNum });
    }
    if (hasW && hasBf && wNum !== 0 && bfNum !== 0) {
      const { fatMassKg, leanMassKg } = deriveMasses({ weightKg: wNum, bodyFatPct: bfNum });
      fatMass.push({ id: d.id, value: fatMassKg });
      leanMass.push({ id: d.id, value: leanMassKg });
    }
    const rawCal = d?.calories;
    const numCal = Number(rawCal);
    if (rawCal != null && rawCal !== '' && Number.isFinite(numCal) && numCal !== 0) {
      calories.push({ id: d.id, value: numCal });
    }
  }
  return { weight, bodyFatPct, fatMass, leanMass, calories };
}

// Estimate caloric deficit over a window using fat and lean mass changes (already smoothed preferred)
export function estimateDeficitKcal(fatLossKg, leanLossKg, windowDays, Efat = HEALTH_CONFIG.ENERGY_PER_KG_FAT_KCAL, Elean = HEALTH_CONFIG.ENERGY_PER_KG_LEAN_KCAL) {
  const total = Math.abs(fatLossKg) * Efat + Math.abs(leanLossKg) * Elean;
  return {
    totalKcal: total,
    avgDailyKcal: windowDays > 0 ? total / windowDays : 0,
  };
}

// Adherence metrics against a calorie target
export function computeAdherence(dailyCaloriesSeries, dailyDeficitSeries, calorieTarget) {
  // dailyCaloriesSeries: [{ id, value }] (reported intake)
  // dailyDeficitSeries: [{ id, value }] (kcal/day estimated)
  const days = Math.min(dailyCaloriesSeries?.length || 0, dailyDeficitSeries?.length || 0);
  if (days === 0 || !Number.isFinite(calorieTarget)) return { percentHit: 0, avgDeviation: 0 };
  let hits = 0, deviations = 0;
  for (let i = 0; i < days; i++) {
    const intake = Number(dailyCaloriesSeries[i].value || 0);
    const deficit = Number(dailyDeficitSeries[i].value || 0);
    const gap = intake - (calorieTarget - deficit); // effective net
    const absDev = Math.abs(gap);
    deviations += absDev;
    if (absDev <= calorieTarget * 0.05) hits++; // within 5%
  }
  const percentHit = (hits / days) * 100;
  const avgDeviation = deviations / days;
  return { percentHit, avgDeviation };
}

export function classifyRate(weightLossPerWeekKg, goal) {
  // goal: 'cutting' | 'bulking'
  if (!Number.isFinite(weightLossPerWeekKg)) return 'unknown';
  if (goal === 'cutting') {
    if (weightLossPerWeekKg < 0.25) return 'lento';
    if (weightLossPerWeekKg <= 1.0) return 'ok';
    return 'rápido';
  } else if (goal === 'bulking') {
    const gain = -weightLossPerWeekKg; // negative indicates gain per week
    if (gain < 0.1) return 'lento';
    if (gain <= 0.5) return 'ok';
    return 'rápido';
  }
  return 'unknown';
}


